import {
  AddIcon,
  Alert,
  LanguageSelector,
  Modal,
  QuestionMarkIcon,
  Severity,
  SubtleButton,
  Tooltip,
  theme,
  usePath,
  withAlpha,
  useToast,
} from '@arnold/common';
import { ProductCode } from '@arnold/core';
import styled from '@emotion/styled';
import classNames from 'classnames';
import { clone } from 'ramda';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import DropdownToggle from 'react-bootstrap/esm/DropdownToggle';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import { Transform } from 'rete-area-plugin/_types/area';
import { useRete } from 'rete-react-plugin';
import {
  Features,
  IsAccessTokenValidQuery,
  Metrics,
  OrganizationQuery,
  TopicDetailQuery,
  TopicGroupDetailQuery,
  UserSysRole,
  useCreateQuestionMetricMutation,
  useDeleteCommentGroupMutation,
  useDeleteQuestionMetricMutation,
  useImportTopicMutation,
  useAiTranslateTopicNodesMutation,
  useMarkTranslationAsReviewedMutation,
  useUpdateCommentGroupPositionMutation,
} from '../../generated/hooks';
import { useFeatureFlag } from '../../lib/helpers/hooks';
import ee from '../../lib/eventEmitter';
import Loading from '../Loading';
import { StyledDropdownItem, StyledDropdownMenu } from '../StyledComponents';
import { ControlPanel } from './ControlPanel';
import { ArielAiSidebar } from './ArielAiSidebar';
import { ArielAiGraphAction } from './ArielAiGraphAction';
import { applyAiDiffHighlights, clearAiDiffHighlights, diffNodeDataGraph } from './arielAiDiff';
import { ArielAiChatResult, useArielAiChat } from './useArielAiChat';
import { TranslationIssuesBanner } from './components/TranslationIssuesBanner';
import { UndoRedoButtons } from './components/UndoRedoButtons';
import { ZoomControls } from './components/ZoomControls';
import { COMMENT_TYPE, NODE_HEIGHT, NODE_WIDTH } from './constants';
import { getMenuItems } from './contextMenu';
import { NodeData } from './dataNode';
import {
  AddLanguageModal,
  DownloadTranslationsModal,
  StyledModalContent,
  UploadTranslationsModal,
} from './importTranslations';
import './index.css';
import {
  CustomNodeEditor,
  createEditor,
  deleteAutosave,
  getAutosavedNodesData,
  processData,
  regenerateReportOrder,
  updatePositionInNodeData,
} from './rete';
import {
  clearEditor,
  cloneGraphNodes,
  fixTopicDetail,
  getNodeTranslationStatus,
  getNodesToCompare,
  getFullGraphSnapshot,
  getTGAccessHeaderContext,
  nodesEqual,
  reportOrdersMatchInitialSnapshot,
  topicDetailToNodes,
  transformNodesForMutation,
  updateNodeDataBasedOnConnections,
  useWarnUserWhenClosingTab,
} from './utils';
import { ValidationError, validateNodes, validateTranslationTexts } from './validation';
import {
  applyTranslationResultToNode,
  getDefaultSourceTranslation,
  getSourceTranslationContent,
} from './translationUtils';

const TopicEditorRoot = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  max-height: 100vh;
  overflow: hidden;
`;

const EditorLayout = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow: hidden;
`;

/** Restores the column flex chain for the canvas (editor-wrapper previously grew directly under no-padding-content). */
const EditorMain = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
`;

type TopicEditorProps = {
  preview?: boolean;
  topicDetail: TopicDetailQuery['topicDetail'] | NonNullable<TopicGroupDetailQuery['topicGroup']['lastValidTopic']>;
  topicGroup: TopicDetailQuery['topicDetail']['topicGroup'];
  organization?: OrganizationQuery['organization'];
  user?: IsAccessTokenValidQuery['isAccessTokenValid']['user'];
  languageCodeOrId?: string;
  limitedMode?: 'READ' | 'WRITE';
  onLanguageChange?: (lang: string) => void;
};

export const TopicEditor: FC<TopicEditorProps> = ({
  topicDetail: topicDetailProp,
  preview = false,
  limitedMode,
  organization,
  user,
  topicGroup,
  languageCodeOrId: languageCodeOrIdFromProps,
  onLanguageChange,
}) => {
  const [updatePosition] = useUpdateCommentGroupPositionMutation();
  const [deleteCommentGroup] = useDeleteCommentGroupMutation();
  const [createMetricQuestionMutation] = useCreateQuestionMetricMutation();
  const [deleteMetricQuestionMutation] = useDeleteQuestionMetricMutation();
  const [markTranslationAsReviewed] = useMarkTranslationAsReviewedMutation();
  const [aiTranslateTopicNodes] = useAiTranslateTopicNodesMutation();

  const { t } = useTranslation('topicEditor');
  const { search } = useLocation();
  const history = useHistory();
  const [editor, setEditor] = useState<CustomNodeEditor | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const { topicEditor, topicEditorPreview } = usePath();
  const { addToast } = useToast();
  const initialNodesRef = useRef<NodeData[]>();
  const [issuesModalShow, setIssuesModalShow] = useState(false);
  const [issuesModalErrors, setIssuesModalErrors] = useState<ValidationError[]>([]);
  const [issuesTestSurveyModalShow, setIssuesTestSurveyModalShow] = useState(false);
  const [addLanguageModalShow, setAddLanguageModalShow] = useState(false);
  const [lastSavedTopicId, setLastSavedTopicId] = useState<string | undefined>();
  const unblockHistoryRef = useRef<() => void>();
  const editorWrapperRef = useRef<HTMLDivElement>(null);
  const [closeModalShow, setCloseModalShow] = useState<{
    open: boolean;
    callback?: (newTopicId?: string) => void;
    changingLanguages?: boolean;
  }>({
    open: false,
    callback: undefined,
    changingLanguages: false,
  });
  const [deleteNodeModalShow, setDeleteNodeModalShow] = useState(false);
  const [deleteNodeModalOnSubmit, setDeleteNodeModalOnSubmit] = useState<() => void>();
  const [fullscreen, setFullsreen] = useState(false);
  const [aiSidebarOpen, setAiSidebarOpen] = useState(false);
  const hasArielAiFeature = useFeatureFlag(Features.ArielAi);
  const [loadAutosaveModalShow, setLoadAutosaveModalShow] = useState<{
    open: boolean;
    callback: (keep: boolean) => void;
    date: Date;
  }>();
  const [isValid, setIsValid] = useState(true);
  const [showCannotEditScheduledAlert, setShowCannotEditScheduledAlert] = useState(true);
  const [showTopicCannotBeScheduledAlert, setShowTopicCannotBeScheduledAlert] = useState(true);
  const [showFreeOrgAlert, setShowFreeOrgAlert] = useState(true);
  const [showSystemTopicWarningAlert, setShowSystemTopicWarningAlert] = useState(true);
  const [questionOrderManuallyModified, setQuestionOrderManuallyModified] = useState(false);
  const [translateViaAi, setTranslateViaAi] = useState<boolean>(false);
  const [nodeStatusOverrides, setNodeStatusOverrides] = useState<Map<string, string>>(() => new Map());
  /** True only after editing report order in the node control (autosave + restore). */
  const manualReportOrderIntentRef = useRef(false);
  const topicGroupQuestionOrderFlagRef = useRef(!!topicGroup.questionOrderManuallyModified);

  useEffect(() => {
    topicGroupQuestionOrderFlagRef.current = topicGroup.questionOrderManuallyModified ?? false;
  }, [topicGroup.questionOrderManuallyModified]);

  const handleCommentDelete = async (commentGroupId: string) => {
    await deleteCommentGroup({
      context: getTGAccessHeaderContext(search),
      variables: {
        id: commentGroupId,
      },
    });
  };

  const createMetricQuestion = async (identifier: Metrics, index: number, topicGroupId: number) => {
    await createMetricQuestionMutation({
      variables: { identifier, index, topicGroupId },
      onCompleted: () => {
        addToast(t('metricQuestionCreated'));
      },
      onError: () => {
        addToast(t('metricQuestionCreationFailed'));
      },
    });
  };

  const deleteMetricQuestion = async (index: number, topicGroupId: number) => {
    await deleteMetricQuestionMutation({
      variables: { index, topicGroupId },
      onCompleted: () => {
        addToast(t('metricQuestionDeleted'));
      },
      onError: () => {
        addToast(t('metricQuestionDeletionFailed'));
      },
    });
  };

  // topicDetail can be sometimes corrupted and data might be incompatible with the topic editor. We need to fix the data before using them
  const topicDetail = useMemo(() => fixTopicDetail(topicDetailProp), [topicDetailProp]);

  const languages = topicDetail.languages
    .map((language) => language.code)
    .filter((langCode) => (!organization ? true : organization.languages.find((lang) => lang.code === langCode)));

  const defaultTopicLanguageCode =
    topicDetail.allQuestions[0].translations.find(
      (translation) => translation.isDefault && languages.includes(translation.language.code),
    )?.language.code || languages[0];
  const [languageCode, setLanguageCode] = useState<string>(() => {
    if (languageCodeOrIdFromProps) {
      const languageCodeFoundInTrans = topicDetail.allQuestions
        .map((q) => q.translations)
        .flat()
        .find(
          (trans) =>
            trans.language.code === languageCodeOrIdFromProps || trans.language.id === languageCodeOrIdFromProps,
        )?.language.code;

      const orgSupportsWantedLanguage = organization?.languages.some((lan) => lan.code === languageCodeFoundInTrans);
      if (languageCodeFoundInTrans && orgSupportsWantedLanguage) {
        return languageCodeFoundInTrans;
      }
    }
    return defaultTopicLanguageCode;
  });
  const [conversationNodes, setConversationNodes] = useState(() =>
    topicDetailToNodes(
      topicDetail.allQuestions,
      languageCode,
      topicDetail.startingQuestion.id,
      topicDetail.commentGroups,
      topicDetail.id,
      search,
    ),
  );
  const translation = topicGroup.translations?.find((translation) => translation.language.code === languageCode);
  const [topicGroupName, setTopicGroupName] = useState(translation?.value || '');
  const [topicGroupDescription, setTopicGroupDescription] = useState(translation?.description || undefined);
  const editorBasicInfoRef = useRef<{ name: string; description: string }>();

  useEffect(() => {
    // set an overflow hidden for the parent to prevent a scrollbar in some weird cases
    // we need to do it like this because parent can be either route or authorized page
    // we don't want Topic Editor specific code in there
    const parent = editorWrapperRef.current?.parentElement;
    if (parent) {
      const initialOverflow = parent.style.overflow;
      parent.style.overflow = 'hidden';
      return () => {
        parent.style.overflow = initialOverflow;
      };
    }
  }, []);

  useEffect(() => {
    editorBasicInfoRef.current = {
      name: topicGroupName,
      description: topicGroupDescription || '',
    };
  }, [topicGroupName, topicGroupDescription]);

  const [processDataFinishedCallback, setProcessDataFinishedCallback] = useState<() => void | undefined>();
  const [editorNodesVersion, setEditorNodesVersion] = useState(0);
  useWarnUserWhenClosingTab(() => !isEditorWithoutChanges());
  const showDeleteNodeModal = (onSubmit: () => void) => {
    setDeleteNodeModalShow(true);
    setDeleteNodeModalOnSubmit(() => onSubmit);
  };
  const [downloadTranslationsModalShow, setDownloadTranslationsModalShow] = useState(false);
  const [uploadTranslationsModalShow, setUploadTranslationsModalShow] = useState(false);
  const [deleteTranslationsModalShow, setDeleteTranslationsModalShow] = useState<
    { languageCode: string; callback: (value: boolean) => void } | undefined
  >(undefined);
  const saveTopicRef = useRef<typeof saveTopic>();

  const languageId = topicDetail.languages.find((lan) => lan.code === languageCode)?.id;

  useEffect(() => {
    setTopicGroupName(translation?.value || '');
    setTopicGroupDescription(translation?.description || undefined);
  }, [translation]);

  useEffect(
    () => {
      const allNodes = topicDetailToNodes(
        topicDetail.allQuestions,
        languageCode,
        topicDetail.startingQuestion.id,
        topicDetail.commentGroups,
        topicDetail.id,
        search,
      );

      if (editor) {
        const commentNodes = editor?.getNodes().filter((node) => node.data.type === COMMENT_TYPE) || [];
        setConversationNodes([
          ...allNodes.filter((node) => node.type !== COMMENT_TYPE).flat(),
          ...commentNodes.map((node) => node.data),
        ]);
      } else {
        setConversationNodes(allNodes);
      }
      setQuestionOrderManuallyModified(topicGroup.questionOrderManuallyModified ?? false);
      manualReportOrderIntentRef.current = false;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [topicDetail.allQuestions, languageCode, topicDetail.startingQuestion.id, topicDetail.id],
  );

  const createAndSetEditor = useCallback(async (el: HTMLElement) => {
    const editor = await createEditor(
      el,
      preview,
      showDeleteNodeModal,
      t,
      topicDetail.id,
      handleCommentDelete,
      createMetricQuestion,
      deleteMetricQuestion,
      topicGroup.id,
      user,
      search,
    );
    editor.onValidityChange = setIsValid;
    setEditor(editor);
    return {
      destroy: () => {
        editor.areaPlugin.destroy();
        editor.removeEventListeners();
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [editorRef] = useRete(createAndSetEditor);

  const hasAiTranslation =
    organization?.featureNames?.includes(Features.AiTranslation) || user?.systemRole === UserSysRole.SysAdmin;
  useEffect(() => {
    if (editor == null) return;
    editor.onReportOrderManuallyChanged = () => {
      manualReportOrderIntentRef.current = true;
      setQuestionOrderManuallyModified(true);
    };
    if (hasAiTranslation) {
      editor.onMarkTranslationAsReviewed = async (questionDefinitionId, languageCode) => {
        await markTranslationAsReviewed({ variables: { questionDefinitionId, languageCode } });
      };
      editor.onAiTranslateNode = async (sourceText, sourceLang, targetLang, choices = [], statements = []) => {
        const { data } = await aiTranslateTopicNodes({
          variables: {
            nodes: [{ questionIndex: 0, questionText: sourceText, choices, statements }],
            sourceLang,
            targetLang,
          },
        });
        if (data?.aiTranslateTopicNodes.limitReached) {
          addToast(t('translationCostLimitReached'), undefined, undefined, undefined, true);
          return {
            questionText: sourceText,
            choices,
            statements,
          };
        }
        const translatedNode = data?.aiTranslateTopicNodes.nodes[0];
        // On (partial) failure the node comes back untranslated — keep the source text instead of blanking it.
        if (!translatedNode?.translated) {
          addToast(t('translationTranslateNodeFailed'), undefined, undefined, undefined, true);
          return {
            questionText: sourceText,
            choices,
            statements,
          };
        }
        return {
          questionText: translatedNode.questionText,
          choices: translatedNode.choices.filter((c): c is string => c != null),
          statements: translatedNode.statements,
        };
      };
    } else {
      editor.onMarkTranslationAsReviewed = undefined;
      editor.onAiTranslateNode = undefined;
    }
    editor.onTranslationStatusChange = (nodeId, status) => {
      setNodeStatusOverrides((prev) => new Map(prev).set(nodeId, status));
    };
    editor.onNodesDataChange = () => {
      setEditorNodesVersion((v) => v + 1);
    };
    editor.getManualReportOrderIntent = () => manualReportOrderIntentRef.current;
    editor.syncQuestionOrderManuallyModifiedAfterHistory = () => {
      if (initialNodesRef.current == null || !editor.loaded) return;
      const reportOrdersAtBaseline = reportOrdersMatchInitialSnapshot(editor, initialNodesRef.current);
      if (reportOrdersAtBaseline) {
        manualReportOrderIntentRef.current = false;
      }
      setQuestionOrderManuallyModified(reportOrdersAtBaseline ? topicGroupQuestionOrderFlagRef.current : true);
    };
    const autosaveData = editor.firstLoad ? getAutosavedNodesData(editor, topicDetail.id, conversationNodes) : null;
    if (autosaveData && autosaveData.language && autosaveData.language !== languageCode) {
      setLanguageCode(autosaveData.language);
      // If the autosaved changes are in a different language than the current one, we need to change the language first
      // and then when the useEffect is called again, the nodes will be loaded in the correct language and hashes can be compared
      // and the user can decide if he wants to keep the autosaved changes
      return;
    }
    const nodesToLoad = (autosaveData && autosaveData.sameHash && autosaveData.nodesData) || conversationNodes;
    const autosaveInitialHash = (autosaveData && autosaveData.hash) || false;
    processData(nodesToLoad, editor, t, languageCode, false, autosaveInitialHash).then(async () => {
      const loadedFromMatchingAutosave =
        autosaveData != null && autosaveData.sameHash && nodesToLoad === autosaveData.nodesData;
      if (loadedFromMatchingAutosave) {
        manualReportOrderIntentRef.current = autosaveData.manualReportOrderIntent;

        setQuestionOrderManuallyModified(
          autosaveData.manualReportOrderIntent || topicGroupQuestionOrderFlagRef.current,
        );
      }

      if (autosaveData && autosaveData?.sameHash) {
        const keepAutosavedNodes = await new Promise<boolean>((resolve) =>
          setLoadAutosaveModalShow({
            open: true,
            callback: resolve,
            date: autosaveData.date,
          }),
        );
        if (keepAutosavedNodes) {
          initialNodesRef.current = [];
        } else {
          await processData(conversationNodes, editor, t, languageCode, false, false);
          deleteAutosave(topicDetail.id);
          manualReportOrderIntentRef.current = false;
          setQuestionOrderManuallyModified(topicGroup.questionOrderManuallyModified ?? false);
        }
      } else {
        deleteAutosave(topicDetail.id);
      }
      if (initialNodesRef.current == null) {
        initialNodesRef.current = getNodesToCompare(editor);
      }
      setEditorNodesVersion((v) => v + 1);
      processDataFinishedCallback?.();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, conversationNodes, processDataFinishedCallback]);

  const [callImportTopicMutation, { loading: loadingMutation }] = useImportTopicMutation({
    context: getTGAccessHeaderContext(search),
    refetchQueries: ['topicDetail'],
  });

  const handleChangeLanguage = async (lang: string, ignoreChanges = false) => {
    if (languageCode === lang) return; // If the language was changed to the language that is currently selected - do nothing
    let newTopicId: string | undefined;
    if (!ignoreChanges && editor && initialNodesRef.current && !isEditorWithoutChanges()) {
      newTopicId = await new Promise<string | undefined>((resolve) => {
        setCloseModalShow({
          open: true,
          callback: resolve,
          changingLanguages: true,
        });
      });
    }
    if (newTopicId) {
      unblockHistoryRef.current?.();
      return topicEditor.toFunc(topicGroup.id, lang, newTopicId);
    }
    onLanguageChange?.(lang) || setLanguageCode(lang);
    const commentNodes = editor?.getNodes().filter((node) => node.data.type === COMMENT_TYPE) || [];
    const allNodes = topicDetailToNodes(
      topicDetail.allQuestions,
      lang,
      topicDetail.startingQuestion.id,
      topicDetail.commentGroups,
      topicDetail.id,
      search,
    );
    setConversationNodes([
      ...allNodes.filter((node) => node.type !== COMMENT_TYPE).flat(),
      ...commentNodes.map((node) => node.data),
    ]);
    initialNodesRef.current = undefined;
    // We can't use react router history here, it has side effects
    // We just want to change the URL to include language, nothing else
    const isPreviewPath = window.location.pathname.includes('topicEditorPreview');
    const pathCreator = isPreviewPath ? topicEditorPreview : topicEditor;
    window.history.replaceState(null, '', pathCreator.createPath(topicGroup.id, lang));
    // wait for the setStates and useEffects to execute and editor to rebuild nodes
    await new Promise((res) => setProcessDataFinishedCallback(() => () => res('')));
  };

  const area = editor?.areaPlugin;

  const timers = new Map<string, NodeJS.Timeout>();

  // Save the position of the comment group when it is moved
  area?.addPipe((context) => {
    if (context.type === 'nodetranslated') {
      const node = editor?.getNode(context.data.id);
      if (node?.data.type === COMMENT_TYPE && preview === false) {
        // Clear the previous timer if it exists
        const previousTimer = timers.get(node.data.questionIndex.toString());
        if (previousTimer) {
          clearTimeout(previousTimer);
        }
        // Start a new timer
        const newTimer = setTimeout(() => {
          if (
            node.data.position[0] !== Math.floor(context.data.position.x) ||
            node.data.position[1] !== Math.floor(context.data.position.y)
          ) {
            if (node.data.comments?.length && node.data.commentGroupId) {
              updatePosition({
                context: getTGAccessHeaderContext(search),
                variables: {
                  id: node.data.commentGroupId.toString(),
                  positionX: Math.floor(context.data.position.x),
                  positionY: Math.floor(context.data.position.y),
                },
              });
            }
            node.data.position = [Math.floor(context.data.position.x), Math.floor(context.data.position.y)];
            node.onChange?.();
          }
        }, 1000); // Wait for 1 second before saving the position
        timers.set(node.data.questionIndex.toString(), newTimer);
      }
    }
    return context;
  });

  const container = area?.container;
  const [hw, hh] = [(container?.clientWidth || 0) / 2, (container?.clientHeight || 0) / 2];
  const { x, y, k }: Transform | Record<string, never> = editor ? editor.areaPlugin.area.transform : {};
  const centerPosition = { x: (hw - x) / k - NODE_WIDTH / 2, y: (hh - y) / k - NODE_HEIGHT / 2 };
  const menuItems = editor && area ? getMenuItems(t, editor, area, topicDetail.id, centerPosition, user, search) : [];
  const shareLinks = (topicGroup.accessKeys || []).map((accessKey) => ({
    url: `${document.location.origin}/${accessKey.level === 'WRITE' ? 'svenEditorPublic' : 'svenPreviewPublic'}/${
      topicGroup.id
    }/${languageCode}?accessKey=${accessKey.accessKey}`,
    level: accessKey.level,
  }));

  const isEditorWithoutChanges = (isTesting = false) => {
    return (
      nodesEqual(initialNodesRef.current, editor!, isTesting) &&
      editorBasicInfoRef.current?.name === translation?.value &&
      (editorBasicInfoRef.current?.description || '') === (translation?.description || '')
    );
  };

  const onSaveButtonClicked = async (isTesting = false): Promise<string | undefined> => {
    if (editor == null) throw new Error('Clicked on save before editor was initialized');
    editor.saveAttemptInitiated();
    if (preview && !limitedMode && !isTesting)
      throw new Error('Something triggered onSave action in preview mode. This should not be possible!');
    updateNodeDataBasedOnConnections(editor.getNodes(), editor.getConnections());
    let errors = validateNodes(editor.getNodes(), editor.getNodes());
    if (isTesting) {
      // We don't want to validate report order when we are only testing the conversation
      errors = errors.filter((e) => e.part !== 'reportOrder');
    }
    if (isEditorWithoutChanges(isTesting)) {
      if (isTesting === false) {
        addToast(t('topicAlreadySaved'), undefined, undefined, undefined, true);
      }
      return lastSavedTopicId ?? topicDetail.id;
    }
    if (errors.length === 0) {
      return saveTopic(isTesting, !isTesting && !limitedMode);
    } else {
      setIssuesModalErrors(errors);
      if (isTesting) {
        setIssuesTestSurveyModalShow(true);
      } else {
        setIssuesModalShow(true);
      }
    }
  };

  const saveTopic = async (
    isTesting = false,
    openNewTopicAfterSave = true,
    isValid = true,
    langCode = languageCode,
    langId = languageId,
    topicName = topicGroupName,
    topicDescription = topicGroupDescription,
    savingNewLanguage = false,
    topicId = topicDetail.id,
    translateViaAi = false,
  ) => {
    editor!.saveAttemptInitiated();
    setIssuesModalShow(false);
    if (topicName.length < 3) {
      topicName = topicGroup.name;
    }
    if (!questionOrderManuallyModified) {
      await regenerateReportOrder(editor!);
    }

    const reportOrderChanged =
      topicGroup.questionOrderModified ||
      !!initialNodesRef.current?.find(
        (node) =>
          node.reportOrder !==
          editor!.getNodes().find((q) => q.data.questionIndex === node.questionIndex)?.data.reportOrder,
      );

    try {
      const transformedTopic = transformNodesForMutation(
        editor!,
        topicName,
        topicGroup.id,
        langCode,
        langId!,
        defaultTopicLanguageCode,
        isTesting,
        topicDescription,
        isValid,
        savingNewLanguage,
        topicId,
        questionOrderManuallyModified,
        reportOrderChanged,
        translateViaAi,
      );
      const result = await callImportTopicMutation({ variables: transformedTopic });
      if (isTesting === false) {
        addToast(t('topicSaved'), undefined, undefined, undefined, true);
        initialNodesRef.current = getNodesToCompare(editor!);
      }
      const newTopicId = result.data && result.data.importTopic.id + '';
      if (openNewTopicAfterSave && newTopicId && !limitedMode) {
        unblockHistoryRef.current?.();
        topicEditor.toFunc(topicGroup.id, langId!);
      }
      editor!.historyPlugin.clear();
      editor!.saved();
      deleteAutosave(topicId, true);
      if (newTopicId) {
        setLastSavedTopicId(newTopicId);
      }
      return newTopicId || undefined;
    } catch (error) {
      addToast(
        t(isTesting === false ? 'topicSavedFail' : 'createTestSurveyFailed'),
        undefined,
        undefined,
        undefined,
        true,
      );
      throw error;
    }
  };
  saveTopicRef.current = saveTopic;

  useEffect(() => {
    if (editor && !preview) {
      const unblock = history.block((tx) => {
        if (closeModalShow.open) {
          return;
        } else if (!isEditorWithoutChanges()) {
          setCloseModalShow({
            open: true,
            callback: () => {
              history.push(tx.pathname);
            },
          });
          return false;
        }
      });

      unblockHistoryRef.current = unblock;
      return () => {
        unblock();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeModalShow.open, editor, translation?.id]);

  const handleTranslateAll = async () => {
    if (!editor) return;
    const allNodes = editor.getNodes().filter((node) => node.data.type !== COMMENT_TYPE);
    const nodeIdsToTranslate = new Set(validateTranslationTexts(allNodes).map((error) => error.node.id));

    allNodes.forEach((node) => {
      const override = nodeStatusOverrides.get(node.id);
      const status =
        override != null ? override : getNodeTranslationStatus(node.data.originalQuestionDefinition, languageCode);
      if (status !== 'ok') {
        nodeIdsToTranslate.add(node.id);
      }
    });

    const nodes = allNodes.filter((node) => nodeIdsToTranslate.has(node.id));
    if (nodes.length === 0) return;
    const sourceLang = nodes[0].data.originalQuestionDefinition?.translations.find((t) => t.isDefault)?.language?.code;
    if (!sourceLang) return;
    const batchNodes = nodes.map((node, i) => {
      const sourceTrans = getDefaultSourceTranslation(node.data);
      const { questionText, choices, statements } = getSourceTranslationContent(sourceTrans);
      return {
        questionIndex: i,
        questionText,
        choices,
        statements,
      };
    });
    try {
      const { data } = await aiTranslateTopicNodes({
        variables: { nodes: batchNodes, sourceLang, targetLang: languageCode },
      });
      if (data?.aiTranslateTopicNodes.limitReached) {
        addToast(t('translationCostLimitReached'), undefined, undefined, undefined, true);
        return;
      }
      const translatedNodes = data?.aiTranslateTopicNodes.nodes;
      if (!translatedNodes) {
        addToast(t('translationTranslateAllFailed'), undefined, undefined, undefined, true);
        return;
      }
      let failedCount = 0;
      translatedNodes.forEach((result, i) => {
        const node = nodes[i];
        if (!node) return;
        // Skip nodes that weren't fully translated (partial failure) — don't overwrite them with blanks
        // or mark them reviewed; leave them untranslated so the user can retry just those.
        if (!result.translated) {
          failedCount += 1;
          return;
        }
        const sourceTrans = getDefaultSourceTranslation(node.data);
        applyTranslationResultToNode(
          node,
          {
            questionText: result.questionText ?? '',
            choices: result.choices,
            statements: result.statements,
          },
          sourceTrans,
        );
        editor.areaPlugin.update('node', node.id);
        setNodeStatusOverrides((prev) => new Map(prev).set(node.id, 'ok'));
      });
      if (failedCount === translatedNodes.length) {
        addToast(t('translationTranslateAllFailed'), undefined, undefined, undefined, true);
      } else if (failedCount > 0) {
        addToast(t('translationTranslateAllPartial'), undefined, undefined, undefined, true);
      }
    } catch {
      addToast(t('translationTranslateAllFailed'), undefined, undefined, undefined, true);
    }
  };

  const handleMarkAllAsOk = async () => {
    if (!editor) return;
    const nodes = editor.getNodes().filter((node) => {
      if (node.data.type === COMMENT_TYPE) return false;
      const override = nodeStatusOverrides.get(node.id);
      const status =
        override != null ? override : getNodeTranslationStatus(node.data.originalQuestionDefinition, languageCode);
      // A missing translation has nothing to review — only source_changed / same_as_source can be marked as OK.
      return status !== 'ok' && status !== 'missing';
    });
    if (nodes.length === 0) return;
    try {
      await Promise.all(
        nodes.map(async (node) => {
          const questionDefinitionId = node.data.originalQuestionDefinition?.id;
          if (!questionDefinitionId) return;
          await markTranslationAsReviewed({ variables: { questionDefinitionId, languageCode } });
        }),
      );
      nodes.forEach((node) => {
        node.data.translationStatusOverride = 'ok';
        editor.areaPlugin.update('node', node.id);
      });
      setNodeStatusOverrides((prev) => {
        const next = new Map(prev);
        nodes.forEach((node) => next.set(node.id, 'ok'));
        return next;
      });
    } catch {
      addToast(t('translationMarkAllAsOkFailed'), undefined, undefined, undefined, true);
    }
  };

  const handleClose = () => {
    if (editor && !isEditorWithoutChanges()) {
      setCloseModalShow({ open: true, callback: undefined });
    } else {
      history.push(`/topics`);
    }
  };

  const handleLanguageDelete = async (lang: string) => {
    const deleteTranslation = await new Promise<boolean>((res) =>
      setDeleteTranslationsModalShow({ callback: res, languageCode: lang }),
    );
    setDeleteTranslationsModalShow(undefined);
    if (deleteTranslation === false) return;
    if (lang === languageCode) {
      await handleChangeLanguage(defaultTopicLanguageCode, true);
    }
    const nodes = editor!.getNodes();
    const nodeData = nodes.map((node) => node.data);
    nodeData.forEach((nodeData) => {
      if (nodeData.originalQuestionDefinition) {
        nodeData.originalQuestionDefinition = {
          ...nodeData.originalQuestionDefinition,
          translations: nodeData.originalQuestionDefinition.translations.filter(
            (trans) => trans.language.code !== lang,
          ),
        };
      }
    });
    const errors = validateNodes(nodes, nodes);
    const isValid = errors.length === 0;
    saveTopicRef.current!(false, true, isValid);
  };

  const MenuItemsDropdown = (
    <Dropdown
      show={isDropdownOpen}
      style={{ top: '-10px' }}
      onToggle={() => setIsDropdownOpen(!isDropdownOpen)}
      drop="right"
    >
      <DropdownToggle className="addNodeButton">
        <SubtleButton
          size="medium"
          color="highlighted"
          data-cy="topic-editor-add"
          data-icom="btn-add-question-ariel"
          leftIcon={<AddIcon />}
        >
          {t('addNode')}
        </SubtleButton>
      </DropdownToggle>
      <StyledDropdownMenu data-icom={'topic-editor-context-menu'}>
        {menuItems.map((item) => (
          <Tooltip title={t(`${item.label}Tooltip`)} key={item.label}>
            <StyledDropdownItem
              key={item.key}
              onClick={item.handler}
              data-cy={`topic-editor-add-${item.label.toLocaleLowerCase()}`}
            >
              {t(item.label)}
            </StyledDropdownItem>
          </Tooltip>
        ))}
      </StyledDropdownMenu>
    </Dropdown>
  );

  const Issues = issuesModalErrors.map((error, i) => (
    <div key={i}>
      <Trans
        i18nKey={`topicEditor:${error.error}`}
        values={{ section: (error.node.data.reportOrder ?? t('withoutReportOrderNumber')!) + '' }}
        components={[<strong></strong>]}
      />
    </div>
  ));

  const IssuesModal = (
    <Modal
      show={issuesModalShow}
      onHide={() => setIssuesModalShow(false)}
      onSubmit={() => {
        saveTopic(false, true, false);
      }}
      title={t('validationIssuesModalTitle')}
      content={
        <>
          <p>{t('validationIssuesModalText1')}</p>
          <p>
            <b>{t('validationIssuesModalText2')}</b>
            {Issues}
          </p>
        </>
      }
      buttons={{
        submit: { title: t('validationIssuesModalSave') },
        cancel: { title: t('cancel'), dataCy: 'btn-cancel-with-errors' },
      }}
    />
  );

  const IssuesTestSurveyModal = (
    <Modal
      show={issuesTestSurveyModalShow}
      onHide={() => setIssuesTestSurveyModalShow(false)}
      onSubmit={() => setIssuesTestSurveyModalShow(false)}
      title={t('validationIssuesTestSurveyModalTitle')}
      content={
        <>
          <p>{t('validationIssuesTestSurveyModalText1')}</p>
          <p>
            <b>{t('validationIssuesModalText2')}</b>
            {Issues}
          </p>
        </>
      }
      buttons={{
        submit: { title: t('common:close') },
      }}
    />
  );

  const CloseModal = (
    <Modal
      show={closeModalShow.open}
      onHide={() => setCloseModalShow({ open: false, callback: undefined })}
      width={540}
      onSubmit={async () => {
        const newTopicId = await saveTopic(false, false);

        if (newTopicId) {
          // pushing new version into the browser history - in order to be able to go back to the version that was just saved (and not the previous one that was being edited)
          topicEditor.toFunc(topicGroup.id, languageId!, newTopicId);
        }

        if (closeModalShow.callback) {
          closeModalShow.callback(newTopicId);
          return;
        }

        if (newTopicId) {
          history.push(`/topics`);
        }
      }}
      hideAfterSubmit={false}
      onCancel={() => {
        deleteAutosave(topicDetail.id, true);
        if (closeModalShow.callback) {
          closeModalShow.callback();
          return;
        }
        history.push(`/topics`);
      }}
      title={t(closeModalShow.changingLanguages ? 'closeModalLanguageChangeTitle' : 'closeModalTitle')}
      text={t(closeModalShow.changingLanguages ? 'closeModalLanguageChangeText' : 'closeModalText')}
      buttons={{
        submit: {
          title: t(
            closeModalShow.changingLanguages ? 'closeModalLanguageChangeSaveAndClose' : 'closeModalSaveAndClose',
          ),
        },
        cancel: { title: t(closeModalShow.changingLanguages ? 'closeModalLanguageChangeClose' : 'closeModalClose') },
      }}
    />
  );

  const DeleteNodeModal = (
    <Modal
      show={deleteNodeModalShow}
      onHide={() => setDeleteNodeModalShow(false)}
      onSubmit={deleteNodeModalOnSubmit}
      title={t('deleteSpecialNodeModalTitle')}
      text={t('deleteSpecialNodeModalText')}
      buttons={{
        submit: { title: t('deleteSpecialNodeModalSubmit') },
        cancel: { title: t('deleteSpecialNodeModalCancel') },
      }}
    />
  );

  const DeleteTranslationModal = (
    <Modal
      show={deleteTranslationsModalShow != null}
      onHide={() => deleteTranslationsModalShow!.callback(false)}
      onSubmit={() => deleteTranslationsModalShow!.callback(true)}
      title={t('deleteTranslationModalTitle')}
      content={
        <>
          <p>{t('deleteTranslationModalText')}</p>
          <StyledModalContent>
            <div className="language">
              <LanguageSelector
                lang={deleteTranslationsModalShow?.languageCode}
                onChange={(lang) => {
                  /* Language should not be changeable */
                }}
                allowedLanguages={[deleteTranslationsModalShow?.languageCode ?? '']}
                isAlignedToLeft
                displayAlways
                margin={`0 0 0 ${theme.spacing.d}`}
                maxWidth={200}
              />
            </div>
          </StyledModalContent>
        </>
      }
      buttons={{
        submit: { title: t('deleteTranslationModalSubmit') },
        cancel: { title: t('modal:cancel') },
      }}
    />
  );

  const LoadAutoSaveModal = (
    <Modal
      show={!!loadAutosaveModalShow?.open}
      onSubmit={() => loadAutosaveModalShow?.callback(true)}
      onClose={() => loadAutosaveModalShow?.callback(true)}
      onCancel={() => loadAutosaveModalShow?.callback(false)}
      onHide={() => setLoadAutosaveModalShow(undefined)}
      hideAfterSubmit={true}
      width={600}
      title={t('loadAutosaveModalTitle')}
      content={
        <>
          <span>
            {t('loadAutosaveModalTextPart1')} <b>{loadAutosaveModalShow?.date.toLocaleDateString()}</b>{' '}
            {t('loadAutosaveModalTextPart2')}
          </span>
          <br />
          <br />
        </>
      }
      buttons={{
        submit: { title: t('loadAutosaveModalSubmit') },
        cancel: { title: t('loadAutosaveModalCancel') },
      }}
    />
  );

  const freeOrgWithCustomTopic =
    !!topicGroup.organization && organization?.productVersion?.product === ProductCode.Free;
  const isCustomTG = !!topicGroup.organization && topicGroup.organization.id === organization?.id;
  const canUseArielAi =
    !preview &&
    !limitedMode &&
    !!topicDetail.isEditable &&
    // Ariel patches change graph structure (nodes/outputs), which is only allowed in the primary
    // language; on secondary languages the editor is restricted to translations.
    defaultTopicLanguageCode === languageCode &&
    (hasArielAiFeature || user?.systemRole === UserSysRole.SysAdmin);

  const getGraph = useCallback(() => {
    if (!editor) {
      return [];
    }
    return getFullGraphSnapshot(editor);
  }, [editor]);

  const handleAiBeforeSend = useCallback(() => {
    if (editor) {
      return clearAiDiffHighlights(editor);
    }
  }, [editor]);

  const handleAiPatchComplete = useCallback(
    async (result: ArielAiChatResult) => {
      if (!editor || !result.patch) {
        return;
      }
      const previousNodes = getFullGraphSnapshot(editor);
      const previousNodesForHistory = cloneGraphNodes(previousNodes);
      const previousByIndex = new Map(previousNodes.map((node) => [node.questionIndex, node]));
      const lockedIndices = new Set(
        editor
          .getNodes()
          .filter((node) => node.lockEdit)
          .map((node) => node.data.questionIndex),
      );
      const patchByIndex = new Map(result.patch.map((node) => [node.questionIndex, node]));
      const mergedPatchNodes = result.patch.map((node) => {
        const existing = previousByIndex.get(node.questionIndex);
        if (lockedIndices.has(node.questionIndex) && existing) {
          return {
            ...clone(existing),
            topicId: topicDetail.id,
          };
        }
        if (node.type === COMMENT_TYPE && existing) {
          return {
            ...clone(existing),
            ...node,
            comments: existing.comments,
            search: existing.search,
            topicId: topicDetail.id,
          };
        }
        if (existing) {
          return {
            ...clone(existing),
            ...node,
            topicId: topicDetail.id,
          };
        }
        return {
          ...node,
          topicId: topicDetail.id,
        };
      });
      const commentsNotInPatch = previousNodes
        .filter((node) => node.type === COMMENT_TYPE && !patchByIndex.has(node.questionIndex))
        .map((node) => clone(node));
      const lockedNodesNotInPatch = previousNodes
        .filter(
          (node) =>
            node.type !== COMMENT_TYPE &&
            lockedIndices.has(node.questionIndex) &&
            !patchByIndex.has(node.questionIndex),
        )
        .map((node) => clone({ ...node, topicId: topicDetail.id }));
      const nextNodes = [...mergedPatchNodes, ...commentsNotInPatch, ...lockedNodesNotInPatch];
      const nextNodesForHistory = cloneGraphNodes(nextNodes);
      const diff = diffNodeDataGraph(previousNodes, nextNodes);
      await processData(nextNodes, editor, t, languageCode, false, false);
      await applyAiDiffHighlights(editor, diff);
      editor.historyPlugin.add(
        new ArielAiGraphAction(editor, previousNodesForHistory, nextNodesForHistory, t, languageCode),
      );
      editor.onHistoryMightHaveChanged();
    },
    [editor, languageCode, t, topicDetail.id],
  );

  const {
    messages: aiMessages,
    streamingText: aiStreamingText,
    isStreaming: aiIsStreaming,
    isPreparingPatch: aiIsPreparingPatch,
    isApplyingPatch: aiIsApplyingPatch,
    sendMessage: sendAiMessage,
    clearMessages: clearAiMessages,
  } = useArielAiChat({
    topicId: topicDetail.id,
    languageCode,
    getGraph,
    onBeforeSend: handleAiBeforeSend,
    onComplete: handleAiPatchComplete,
  });

  const handleAiNewConversation = useCallback(() => {
    clearAiMessages();
    if (editor) {
      clearAiDiffHighlights(editor);
    }
  }, [clearAiMessages, editor]);

  const translationIssues = useMemo(() => {
    const issues = { total: 0, reviewable: 0, missingText: 0 };

    if (defaultTopicLanguageCode === languageCode) {
      return issues;
    }

    const nodes = (editor?.getNodes() ?? []).filter((node) => node.data.type !== COMMENT_TYPE);
    const emptyTextNodeIds = new Set(validateTranslationTexts(nodes).map((error) => error.node.id));
    issues.missingText = emptyTextNodeIds.size;

    const statusIssueNodeIds = new Set<string>();
    if (hasAiTranslation) {
      nodes.forEach((node) => {
        const override = nodeStatusOverrides.get(node.id);
        const status =
          override != null ? override : getNodeTranslationStatus(node.data.originalQuestionDefinition, languageCode);
        if (status === 'ok') return;
        statusIssueNodeIds.add(node.id);
        if (status !== 'missing') issues.reviewable += 1;
      });
    }

    issues.total = new Set([...emptyTextNodeIds, ...statusIssueNodeIds]).size;
    return issues;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAiTranslation, defaultTopicLanguageCode, languageCode, editor, editorNodesVersion, nodeStatusOverrides]);

  return (
    <>
      <TopicEditorRoot>
        <ControlPanel
          languages={languages}
          defaultTopicLanguageCode={defaultTopicLanguageCode}
          shareLinks={shareLinks}
          topicGroupId={topicGroup.id}
          selectedLanguage={languageCode}
          topicGroupTranslation={topicGroupName}
          topicGroupDescription={topicGroupDescription}
          handleLanguageChange={(lang: string) =>
            lang === 'addLanguage' ? setAddLanguageModalShow(true) : handleChangeLanguage(lang)
          }
          handleLanguageDelete={handleLanguageDelete}
          topicId={topicDetail.id}
          limitedMode={limitedMode}
          preview={preview}
          isEditable={!!topicDetail.isEditable}
          isValid={isValid}
          isCustom={isCustomTG}
          freeOrgWithCustomTopic={freeOrgWithCustomTopic}
          setTopicGroupName={setTopicGroupName}
          setTopicGroupDescription={setTopicGroupDescription}
          onClose={handleClose}
          onSave={onSaveButtonClicked}
          onRearrange={() => editor?.rearrange()}
          onClear={() => editor && clearEditor(editor, t, languageCode, handleCommentDelete)}
          onRegenerateReportOrder={() => editor && regenerateReportOrder(editor)}
          onDownloadTranslations={() => setDownloadTranslationsModalShow(true)}
          onUploadTranslations={() => setUploadTranslationsModalShow(true)}
          nodes={editor?.getNodes()}
          nodeStatusOverrides={nodeStatusOverrides}
          onTranslateAll={hasAiTranslation ? handleTranslateAll : undefined}
          hasAiTranslation={hasAiTranslation}
        />
        <EditorLayout>
          <EditorMain>
            <div
              ref={editorWrapperRef}
              className={classNames(
                'editor-wrapper',
                defaultTopicLanguageCode !== languageCode && 'notDefaultLanguage',
                fullscreen && 'arielFullscreen',
                fullscreen && aiSidebarOpen && 'arielFullscreenWithAiSidebar',
              )}
              data-cy="sven-page"
              data-topic-id={topicDetail.id}
            >
              {!preview && MenuItemsDropdown}
              <UndoRedoHelpContainer>
                {editor && <UndoRedoButtons editor={editor} />}
                <SubtleButton
                  size="small"
                  iconColor={theme.colors.actionPrimary.default}
                  onClick={() => ee.emitArielHintsModal()}
                  data-icom={'btn-help-ariel'}
                  leftIcon={<QuestionMarkIcon />}
                ></SubtleButton>
              </UndoRedoHelpContainer>
              <AlertContainer>
                {hasAiTranslation && translationIssues.total > 0 && (
                  <TranslationIssuesBanner
                    issueCount={translationIssues.total}
                    reviewableCount={translationIssues.reviewable}
                    hasAiTranslation={hasAiTranslation}
                    onTranslateAll={handleTranslateAll}
                    onMarkAllAsOk={handleMarkAllAsOk}
                  />
                )}
                {isCustomTG && !topicDetail.isEditable && preview && showCannotEditScheduledAlert && (
                  <Alert severity={Severity.WARNING} onClose={() => setShowCannotEditScheduledAlert(false)}>
                    {t('cannotEditScheduled')}
                  </Alert>
                )}
                {!isValid && showTopicCannotBeScheduledAlert && (
                  <Alert severity={Severity.WARNING} onClose={() => setShowTopicCannotBeScheduledAlert(false)}>
                    {t('topicOverview:topicCannotBeScheduled')}
                  </Alert>
                )}
                {freeOrgWithCustomTopic && showFreeOrgAlert && (
                  <Alert severity={Severity.WARNING} onClose={() => setShowFreeOrgAlert(false)}>
                    {t('surveysOverview:freeOrganizationsCannotPlanCustomTopics')}
                  </Alert>
                )}
                {!isCustomTG &&
                  user?.systemRole === UserSysRole.SysAdmin &&
                  !preview &&
                  showSystemTopicWarningAlert && (
                    <Alert severity={Severity.DANGER} onClose={() => setShowSystemTopicWarningAlert(false)}>
                      {t('systemTopicEditWarning')}
                    </Alert>
                  )}
              </AlertContainer>

              {editor && (
                <ZoomControls
                  editor={editor}
                  userId={user?.id}
                  fullscreen={fullscreen}
                  setFullscreen={setFullsreen}
                  showAiEditor={canUseArielAi}
                  aiSidebarOpen={aiSidebarOpen}
                  onToggleAiEditor={() => setAiSidebarOpen((open) => !open)}
                />
              )}
              <div ref={editorRef} id="rete-editor" className={classNames('custom-editor', preview && 'preview')}></div>
              <SelectionRectangle id="selection-rectangle" />
            </div>
          </EditorMain>
          {canUseArielAi && (
            <ArielAiSidebar
              open={aiSidebarOpen}
              fullscreen={fullscreen}
              topicName={topicGroupName}
              languageCode={languageCode}
              messages={aiMessages}
              streamingText={aiStreamingText}
              isStreaming={aiIsStreaming}
              isPreparingPatch={aiIsPreparingPatch}
              isApplyingPatch={aiIsApplyingPatch}
              onSend={sendAiMessage}
              onNewConversation={handleAiNewConversation}
            />
          )}
        </EditorLayout>
      </TopicEditorRoot>
      {IssuesModal}
      {IssuesTestSurveyModal}
      {CloseModal}
      {DeleteNodeModal}
      {DeleteTranslationModal}
      {LoadAutoSaveModal}
      <DownloadTranslationsModal
        show={downloadTranslationsModalShow}
        setShow={setDownloadTranslationsModalShow}
        nodes={editor?.getNodes()}
        topicName={topicGroupName}
        languageCode={languageCode}
      />
      <UploadTranslationsModal
        show={uploadTranslationsModalShow}
        setShow={setUploadTranslationsModalShow}
        nodes={editor?.getNodes()}
        languageCodes={[languageCode]}
        onTranslationsImported={async (importedLanguageCode: string) => {
          const nodes = editor!.getNodes();
          updatePositionInNodeData(editor!, nodes);
          const nodesData = nodes.map((node) => node.data);
          setConversationNodes(nodesData);
        }}
      />
      <AddLanguageModal
        show={addLanguageModalShow}
        setShow={setAddLanguageModalShow}
        languageCodes={[...new Set(organization?.languages.map((lang) => lang.code) || [])].filter((lang) =>
          topicDetail.languages.every((lang2) => lang !== lang2.code),
        )}
        onLanguageAdded={(languageCode: string, title: string, description: string) => {
          const languageId = organization!.languages.find((lang) => lang.code === languageCode)!.id;
          saveTopic(false, true, false, languageCode, languageId, title, description, true, undefined, translateViaAi);
        }}
        orgPrimaryLang={defaultTopicLanguageCode}
        translateViaAi={translateViaAi}
        setTranslateViaAi={setTranslateViaAi}
        hasAiTranslation={hasAiTranslation}
      />
      {loadingMutation && <Loading />}
    </>
  );
};
const UndoRedoHelpContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.d};
  position: absolute;
  bottom: ${theme.spacing.f};
  left: ${theme.spacing.f};
  z-index: 10;
`;

const AlertContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.d};
  position: absolute;
  top: ${theme.spacing.f};
  left: 50%;
  transform: translateX(-50%);
  z-index: 5;
  max-width: 387px;
`;

const SelectionRectangle = styled.div`
  display: none;
  position: absolute;
  border: 1px solid ${theme.colors.actionPrimary.default};
  z-index: 1;
  // color must be in RGBA coz of opacity
  background-color: ${withAlpha(theme.colors.actionPrimary.default, 0.07)};
`;
